import { useState, type ReactNode } from 'react'
import { useSlotT } from '@/cms/useSlotT'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import { Link } from 'react-router-dom'
import { Container, Card, Reveal } from '@/components/ui'
import { H3 } from '@/components/ui/typography'
import { cmsProducts } from '@/content/cms/products.generated'
import { localizeProducts, type LocalizedProduct } from '@/content/cms/productLocale'
import CmsImage from '@/components/media/CmsImage'
import { cmsMediaLargestWebpUrl } from '@/utils/cmsMediaUrl'
import {
  isStaticProductPage,
  productExternalCta,
  productRoute,
} from '@/content/productRoutes'
import { buildLocalePath, type Locale } from '@/utils/localePath'

/**
 * 產品與服務 — the product catalogue section of the /products page.
 *
 * RENDERED BY THE PAGE. `src/pages/ProductsPage.tsx` imports this
 * component and places it directly under the page header: the catalogue
 * is what the page is for, so the products come first and the
 * organisation's story lives on /about.
 *
 * The section lives in its own file so the page's own markup stays
 * readable. Its copy (`products.offers.*`, `products.cta`) comes from the
 * i18n files and the CMS slots.
 *
 * The card layout below is unchanged from when it was on the page — read
 * the comments here for why the flagship cards are laid out as they are.
 */

/**
 * The `sizes` for an image in a 3-up grid (`md:grid-cols-3`).
 *
 * A column is roughly a third of the container, which is ≈33vw on a
 * phone-to-desktop sweep and ≈384px on a wide screen — where 33vw
 * (≈475px) overstates it. Overstating is safe; understating would make
 * the browser upscale.
 */
const GRID_3_IMAGE_SIZES = '(min-width: 768px) 33vw, 100vw'

/**
 * The `sizes` for an image in the 2-up system grid (`md:grid-cols-2`).
 *
 * A column is half the container minus the 20px gap: ≈566px on the widest
 * container (1200 − 48 padding − 20 gap), and `50vw` in between. Both
 * overstate slightly at the top end, which is the safe direction — the
 * browser never upscales from a rung smaller than the slot.
 */
const GRID_2_IMAGE_SIZES = '(min-width: 1280px) 570px, (min-width: 768px) 50vw, 100vw'

/**
 * Products whose artwork is a SQUARE poster (2800×2800).
 *
 * The media boxes on this page are 16:9 (flagship cards) and 4:3 (service
 * cards), and they fill with `object-cover` — which crops a square poster
 * down to its middle band and cuts off the title line and the bottom row of
 * cards. These products render `object-contain` instead: the poster shows
 * whole, and the box's own background fills the letterbox bands.
 *
 * The BOX ratio is deliberately NOT changed. The flagship cards sit in a
 * subgrid whose rows are shared with the cards beside them, so a media band
 * of a different height would push this card's colour band out of line with
 * its neighbours.
 */
const SQUARE_POSTER_SLUGS = new Set(['values-incubator'])

/**
 * Feature rows that must NOT be rendered on a product card, keyed by
 * product slug, holding the ZERO-BASED index of the feature in the CMS
 * array.
 *
 * `national-education` drops its 5th feature (「多語支援」/ 「多语支援」/
 * "Bilingual" — bilingual content matching the school's language of
 * instruction). It is hidden in every locale, so the card shows the same
 * five points in zh-Hant, zh-Hans and en.
 */
const HIDDEN_FEATURE_INDEXES: Record<string, number[]> = {
  'national-education': [4],
}

/** A feature row, normalised to the object shape the cards render. */
interface ProductFeature {
  title: string
  text: string
}

/**
 * The separator between a feature's title and its explanation in the
 * plain-string shape: a space, an EM DASH, a space.
 *
 * It is the em dash (U+2014) on its own — the CMS copy also contains
 * EN DASHes (U+2013, in "home–school") and NON-BREAKING HYPHENS
 * (U+2011, in "M‑Card"), and those must not split a line.
 */
const FEATURE_SEPARATOR = ' — '

/**
 * Normalise one CMS feature into `{ title, text }`.
 *
 * The CMS holds the features in TWO shapes: an object per feature
 * (`{ title, text }`) for most products, and a plain string
 * ("Title — explanation") for ValuesIncubator+. The two used to render
 * through separate branches, so the flagship cards on this page showed
 * two different feature lists side by side — the object ones with a
 * bold title over an explanatory line, the string ones as a single
 * line of text in a tighter list. Splitting the string on its em dash
 * lets every card render the same markup.
 */
function toProductFeature(raw: unknown): ProductFeature {
  if (typeof raw === 'string') {
    const idx = raw.indexOf(FEATURE_SEPARATOR)
    if (idx === -1) return { title: raw.trim(), text: '' }
    return {
      title: raw.slice(0, idx).trim(),
      text: raw.slice(idx + FEATURE_SEPARATOR.length).trim(),
    }
  }
  const obj = (raw ?? {}) as { title?: string; text?: string }
  return { title: obj.title ?? '', text: obj.text ?? '' }
}

/**
 * A feature list on a product card.
 *
 * The FIRST feature is always visible; the rest sit behind a 「顯示更多」
 * button. The list used to open entirely collapsed behind a 「展開功能」
 * button — so the only thing on screen was a button that said nothing
 * about the product, and a visitor had no reason to press it. Showing one
 * feature first gives the card something to SAY, and gives the button a
 * reason to exist: it now visibly reveals the five features hidden under
 * it.
 *
 * The button sits BELOW the list rather than above it, for the same
 * reason: the eye reads feature → button, and the chevron points at the
 * items it unfolds.
 *
 * The label is localised (`products.offers.featureToggle.*`) — the old
 * one was a hard-coded Chinese string, so an English or Simplified
 * visitor on /en/ or /zh-Hans/ read 「展開功能」 on an English page.
 */
function ProductFeatureList({
  features,
  idPrefix,
}: {
  features: ProductFeature[]
  /** The product slug, used to build a stable id for `aria-controls`. */
  idPrefix: string
}) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  // A card with a single feature has nothing to hide — the button would
  // only be noise.
  const collapsible = features.length > 1
  const visible = expanded ? features : features.slice(0, 1)
  const listId = `features-${idPrefix}`
  return (
    <div className="p-5 md:p-6">
      <ul id={listId} className="space-y-4">
        {visible.map((feat, i) => (
          <li key={i} className="flex items-start gap-3 text-ink-soft">
            <span className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <Icon icon="lucide:check" className="text-primary text-xs" />
            </span>
            <div>
              <h4 className="font-bold text-ink leading-snug">{feat.title}</h4>
              {feat.text && <p className="text-sm leading-relaxed">{feat.text}</p>}
            </div>
          </li>
        ))}
      </ul>
      {collapsible && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls={listId}
          className="mt-4 inline-flex items-center gap-2 border-2 border-primary text-primary px-5 py-2 rounded-yada-full font-semibold text-sm transition-colors hover:bg-primary hover:text-surface focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
        >
          <span>
            {expanded
              ? t('products.offers.featureToggle.less')
              : t('products.offers.featureToggle.more')}
          </span>
          <Icon
            icon={expanded ? 'lucide:chevron-up' : 'lucide:chevron-down'}
            className="flex-shrink-0"
          />
        </button>
      )}
    </div>
  )
}

/**
 * The anchor inside a product card's TITLE.
 *
 * Most products' titles are plain text and the card's button is the way
 * in. A product whose call to action leaves this site (see
 * `productExternalCta` — today only CSP, which lives on csp.yada.hk)
 * still has a page of its own HERE, so its title becomes the link to
 * that page: without it the product would have no on-site entry point at
 * all, only the off-site button.
 *
 * The HEADING element stays exactly where it was and the anchor sits
 * INSIDE it, so the card's DOM structure — and the shared subgrid track
 * the heading occupies — is untouched. The anchor carries the heading's
 * own colour (`a { color: var(--color-primary) }` in the base layer would
 * otherwise paint a green link on the green band), and underlines on
 * hover only.
 */
function CardTitleLink({
  slug,
  href,
  className,
  children,
}: {
  slug: string
  /** The product's OWN page on this site, locale-prefixed. */
  href: string
  /** Colour + hover/focus classes for the anchor. */
  className: string
  children: ReactNode
}) {
  if (!productExternalCta(slug)) return <>{children}</>
  return (
    <Link to={href} className={className}>
      {children}
    </Link>
  )
}

export default function ProductOffersSection() {
  const slotT = useSlotT('products')
  const { i18n } = useTranslation()
  // Internal links carry the active locale prefix — a bare
  // `/products/harbour` would be bounced by the bare-path redirect map
  // into the default locale and drop a visitor who is reading English.
  const locale = (i18n.language || 'zh-Hant') as Locale

  // The generated index carries all three locales' copy; resolve it once
  // here so every card, table row and price card below reads the
  // active locale's text.
  const products = localizeProducts(cmsProducts, locale)

  const systemProducts = products.filter((p) => p.category === 'system')
  const toolItems = products.filter((p) => p.category === 'tool')

  /**
   * 服務項目 — the service line items a school can buy.
   *
   * Every product appears on this page exactly ONCE, with ONE
   * introduction: SmartCindy already has its flagship system card above
   * (with the hero image), so listing it again here read as a duplicate
   * entry. Harbour is a service row and links to /products/harbour, like
   * every other card.
   */
  const serviceItems = products.filter((p) => p.category === 'service')

  /**
   * The link a product card must point at: the product's OWN page.
   *
   * Every product has a page of its own, so a card's call to action
   * leads there — not to the CMS `cta_link`, which is a `mailto:` for
   * most rows and reads on a card as "this product has no page".
   *
   * The locale prefix is mandatory: a bare `/products/harbour` is
   * bounced by the bare-path redirect map into the DEFAULT locale, so a
   * visitor reading English would be dropped into Chinese.
   *
   * BiteLearn is served as a static HTML page by nginx rather than by
   * React Router, so it needs a plain <a> — a <Link> would let React
   * Router intercept the navigation and 404 it. Its href keeps the
   * trailing slash that resolves against the directory's index.html.
   */
  const productHref = (slug: string): string =>
    isStaticProductPage(slug)
      ? buildLocalePath(`/products/${slug}/`, locale)
      : buildLocalePath(productRoute(slug), locale)

  // Render a flagship EdTech (system) product card. Self-guards on
  // system.image_uuid (joined via the media index), so the same card body
  // works for both the image-header variant and the text-only variant.
  // Per-locale copy is already resolved by cms-sync — title/description/
  // badge are plain strings, features is the runtime-discriminated array.
  //
  // The card lives in a 2-up grid (see 2a below): every system product
  // shares one row with another product, so the body is sized for a
  // ~570px column — image bleeding to the card edge, a compact hero band,
  // a single-column feature list and the CTA pinned to the bottom.
  //
  // ROW ALIGNMENT — WHY THE CARD IS A GRID, NOT A FLEX COLUMN
  // --------------------------------------------------------
  // The four system cards carry wildly different copy: the flagship title
  // is one line in zh-Hant but two in en; SmartCindy's description is two
  // lines in zh-Hant but three in en. As a flex column that made the
  // gradient band 167px tall on one card and 199px on its neighbour — the
  // colour blocks in the same visual row started at different heights and
  // the page read as broken rather than as a grid.
  //
  // The fix is a SHARED ROW TRACK, not a fixed pixel height. The parent
  // grid (see 2a) declares no explicit rows; each card spans FOUR of its
  // implicit rows — media / band / features / CTA — and lays itself out
  // over them with `grid-rows-subgrid`. The two cards in a visual row
  // share those four rows, and an implicit row is sized to the TALLEST
  // item in it, so the gradient band is exactly as tall on both cards
  // (199px in zh-Hant, 214px in en) and the feature list below it starts
  // at the same y. Nothing is hard-coded, so a longer title or a third
  // locale still lands correctly.
  //
  // The subgrid is what makes the CTA land on the same baseline too: the
  // FEATURES track is the only one whose content is unbounded (6 items,
  // each with a title and a sentence), so it absorbs the row's slack and
  // pushes every CTA down to the same line.
  const renderSystemCard = (system: LocalizedProduct, sIdx: number) => {
    const rawFeatures = system.features ?? []
    // Drop the features listed in HIDDEN_FEATURE_INDEXES before rendering
    // (index-based, so it works for both the string[] and the
    // { title, text }[] shapes the CMS can hold).
    const hidden = HIDDEN_FEATURE_INDEXES[system.slug] ?? []
    const visibleFeatures = rawFeatures.filter((_, idx) => !hidden.includes(idx))
    // Normalise the CMS's two feature shapes into `{ title, text }` so
    // every flagship card renders the same list.
    const features = visibleFeatures.map(toProductFeature)
    // "Has an image" is now "has a WebP rung" — the shared <CmsImage> never
    // renders an <img> pointing at a non-image (a PDF cover would have been
    // a broken image), so the card falls back to its icon tile in that case.
    const hasImage = Boolean(
      system.image_uuid && cmsMediaLargestWebpUrl(system.image_uuid),
    )
    // A square poster cannot be cropped into a 16:9 band — see
    // SQUARE_POSTER_SLUGS.
    const fit = SQUARE_POSTER_SLUGS.has(system.slug) ? 'object-contain' : 'object-cover'
    // The product's button may point at ANOTHER website (CSP → csp.yada.hk).
    // Null for every product that stays on this site.
    const externalCta = productExternalCta(system.slug)
    return (
      <Reveal
        key={system.slug}
        delay={sIdx * 0.05}
        // The Reveal wrapper IS the grid item, so it carries the row span
        // and the subgrid declaration. `h-full` alone would not do it: the
        // wrapper has to line its own tracks up with the parent's.
        //
        // `mb-5` is the 20px between two rows of cards — the grid's own
        // row gap is 0, because a row gap on this grid would open a hole
        // between the cover image and the colour band INSIDE each card.
        className="h-full mb-5 row-span-4 grid grid-rows-subgrid"
      >
        <Card
          bordered
          // `flush` — the image and the colour band run to the card edge;
          // every block inside brings its own padding.
          density="flush"
          id={`product-${system.slug}`}
          className="h-full overflow-hidden scroll-mt-[var(--sticky-header-offset)] row-span-4 grid grid-rows-subgrid"
        >
          {/* Media band — the product's cover image, or a gradient panel
              that stands in for it.
              BOTH variants occupy the SAME grid track (see the subgrid
              explanation above), so a product without a photo still lines
              its colour band up with its neighbours. Without the panel the
              card would start straight at the band, and its band would sit
              ~330px higher than the ones beside it. */}
          <div className="relative bg-surface-3 overflow-hidden">
            {hasImage ? (
              <CmsImage
                uuid={system.image_uuid}
                // An EMPTY product alt falls back to the media library's
                // alt for the reader's script — `alt=""` would mark a
                // content photo as decorative.
                alt={system.image_alt || undefined}
                sizes={GRID_2_IMAGE_SIZES}
                className={`block w-full h-auto aspect-[16/9] ${fit}`}
              />
            ) : (
              // No photo: a gradient panel in the product's own accent
              // colours, with the icon as a watermark. Same 16:9 box as
              // the photo above, so the two variants are interchangeable.
              <div
                className={`w-full aspect-[16/9] bg-gradient-to-br ${system.accent_gradient} flex items-center justify-center`}
              >
                <Icon
                  icon={system.icon}
                  className="text-surface/50"
                  style={{ fontSize: '4.5rem' }}
                />
              </div>
            )}
          </div>
          {/* Hero band — icon + badge on the first line, title below it.
              The band's own height is decided by the shared grid track, so
              it is the same height on every card in a row. */}
          <div className={`p-5 md:p-6 bg-gradient-to-br ${system.accent_gradient}`}>
            <div className="flex flex-wrap items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-yada-lg bg-surface/20 flex items-center justify-center shrink-0">
                <Icon
                  icon={system.icon}
                  className="text-surface"
                  style={{ fontSize: '1.25rem' }}
                />
              </div>
              {system.badge && (
                <span className="inline-block bg-accent text-ink px-3 py-1 rounded-yada-full text-xs font-bold">
                  {system.badge}
                </span>
              )}
            </div>
            {/* The title keeps H3's own scale (18/20px) — the site-wide
                card-title size. `text-surface` is required: the global
                `:where(h1…h6)` rule paints headings with the ink colour, and
                a heading inherits nothing from its parent when it carries a
                colour rule of its own — so without it the title would read
                dark-on-green.

                For a product whose button leaves the site (CSP), the title
                is the on-site link — see CardTitleLink. The anchor repeats
                `text-surface` because the base `a` rule would paint it
                primary-green on this green band. */}
            <H3 className="text-surface">
              <CardTitleLink
                slug={system.slug}
                href={productHref(system.slug)}
                className="text-surface hover:text-surface hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-surface"
              >
                {system.title}
              </CardTitleLink>
            </H3>
            <p className="mt-2 text-surface/90 leading-relaxed text-sm md:text-base">
              {system.description}
            </p>
          </div>
          {/* Feature list — one column. In a half-width card a 2- or 3-up
              grid would leave ~180px per cell, too narrow for the feature
              title + explanation to read as a sentence.

              ONE MARKUP FOR EVERY CARD: the CMS holds the features in two
              shapes (see `toProductFeature`) and they are normalised to
              `{ title, text }` before rendering, so this list looks the
              same on every flagship card — a bold title over its
              explanation — and the rows line up across the grid. */}
          <ProductFeatureList features={features} idPrefix={system.slug} />
          <div className="px-5 md:px-6 pb-5 md:pb-6 self-end">
            {/*
              A product whose call to action lives on ANOTHER website
              (CSP → csp.yada.hk) gets a plain <a> to that site — a
              <Link> would send the visitor to an internal route instead
              of the product's own site. The URL is validated by
              `productExternalCta` before it reaches the href.

              BiteLearn renders as a static HTML page served by nginx
              (dist/products/bite-learn/index.html), not as a React
              route. Use a plain <a> for that slug so the browser hits
              the server instead of React Router 404'ing it.
            */}
            {externalCta ? (
              <a
                href={externalCta}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-primary text-surface px-6 py-3 rounded-yada-full font-semibold hover:bg-primary-dark transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
              >
                {slotT('products.cta')}
                <Icon icon="lucide:arrow-right" />
              </a>
            ) : isStaticProductPage(system.slug) ? (
              <a
                href={productHref(system.slug)}
                className="inline-flex items-center gap-2 bg-primary text-surface px-6 py-3 rounded-yada-full font-semibold hover:bg-primary-dark transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
              >
                {slotT('products.cta')}
                <Icon icon="lucide:arrow-right" />
              </a>
            ) : (
              <Link
                to={productHref(system.slug)}
                className="inline-flex items-center gap-2 bg-primary text-surface px-6 py-3 rounded-yada-full font-semibold hover:bg-primary-dark transition-colors focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
              >
                {slotT('products.cta')}
                <Icon icon="lucide:arrow-right" />
              </Link>
            )}
          </div>
        </Card>
      </Reveal>
    )
  }

  return (
    <section className="py-section-y px-section-x bg-surface" aria-labelledby="offers-title">
      <Container>
        <Reveal>
          <div className="text-center mb-8">
            {/* Same as the 服務範疇 section above: the numbered tagline
                (「二、我們的產品與服務」) is deleted, not hidden — the
                title below carries the section's name, and the copy
                survives in the CMS slot (`products.offers.tag`). */}
            <h2 id="offers-title" className="section-title__title text-3xl md:text-4xl text-ink">
              {slotT('products.offers.title')}
            </h2>
          </div>
        </Reveal>

        <div className="space-y-7">
          {/* 2a. 旗艦 EdTech 系統 (system category)

              Two products per row. The flagship used to own a full row on
              its own — a 1152px-wide card, ~1100px tall — so every product
              below it started below the fold and the section ran to more
              than three screens. The card body (see `renderSystemCard`) is
              sized for a half-width column.

              THE ROW TRACKS
              --------------
              The grid declares NO explicit rows, and that is the whole
              trick. Each card spans FOUR implicit rows (media / band /
              features / CTA) via `row-span-4` + `grid-rows-subgrid`:

                row 1   the media band — the 16:9 cover image
                row 2   the gradient band
                row 3   the feature list
                row 4   the CTA

              An implicit row is sized to the TALLEST item in it, and the
              two cards in a visual row SHARE those four rows. So the
              gradient band of both cards is stretched to the same height
              (199px in zh-Hant, 214px in en), the feature lists below
              them start on the same line, and the CTAs land on the same
              baseline — with no pixel value hard-coded anywhere, so a
              longer title or a third locale still lands correctly.

              `md:auto-rows-fr` was tried here and removed: it made the
              SECOND row group's implicit rows `fr`-sized, so the band
              track there swallowed the whole row and grew to 554px. The
              implicit `auto` sizing is what keeps the band sized by its
              content.

              THE GAPS
              --------
              `gap-x-5` only — the ROW gap is zero, and the 20px between
              two rows of cards comes from `mb-5` on each card instead.
              A row gap on this grid is not the gap between two cards: it
              is the gap between the four TRACKS inside a card, so a
              20px row gap opened a 20px hole between the cover image
              and the colour band of every single card.

              `mb-5` on the card puts the separation back where it
              belongs — between one row-group of cards and the next —
              without touching the tracks inside a card.

              Below `md` the grid is a single column, where each card is
              alone in its row group and the tracks resolve to its own
              content — the phone layout is unaffected. */}
          <div className="grid md:grid-cols-2 gap-x-5 gap-y-0">
            {systemProducts.map((system, sIdx) => renderSystemCard(system, sIdx))}
          </div>

          {/* 2b. 服務項目 */}
          <div>
            <h3 className="text-xl md:text-2xl font-bold text-ink mb-5">
              {slotT('products.offers.services.title')}
            </h3>
            <div className="grid md:grid-cols-3 gap-gap-cards">
              {serviceItems.map((product, idx) => {
                // A service that ships artwork shows it in place of the
                // icon tile, so the card reads as the product rather than
                // as a generic line item.
                const hasImage = Boolean(
                  product.image_uuid && cmsMediaLargestWebpUrl(product.image_uuid),
                )
                // Same square-poster rule as the flagship cards above.
                const fit = SQUARE_POSTER_SLUGS.has(product.slug) ? 'object-contain' : 'object-cover'
                // The card's call to action leads to the product's OWN
                // page — the CMS `cta_link` is a mailto: on most rows and
                // hides the fact that the product has a page of its own.
                const href = productHref(product.slug)
                // A product whose button points at ANOTHER website (CSP).
                // Null for every product that stays on this site.
                const externalCta = productExternalCta(product.slug)
                return (
                  <Reveal key={product.slug} delay={idx * 0.1}>
                    {/* The id is `service-` prefixed: the same slug may
                        also render as a flagship card above, and a DOM id
                        must be unique. */}
                    <Card hover bordered id={`service-${product.slug}`} className="h-full flex flex-col scroll-mt-[var(--sticky-header-offset)]">
                      {hasImage ? (
                        <div className="aspect-[4/3] mb-5 rounded-yada-lg overflow-hidden bg-surface-2">
                          <CmsImage
                            uuid={product.image_uuid}
                            // An EMPTY product alt falls back to the media
                            // library's alt for the reader's script.
                            alt={product.image_alt || undefined}
                            sizes={GRID_3_IMAGE_SIZES}
                            className={`w-full h-full ${fit}`}
                          />
                        </div>
                      ) : (
                        <div
                          className={`w-16 h-16 rounded-yada-xl bg-gradient-to-br ${product.accent_gradient} flex items-center justify-center mb-6 shadow-yada`}
                        >
                          <Icon
                            icon={product.icon}
                            className="text-surface"
                            style={{ fontSize: '2rem' }}
                          />
                        </div>
                      )}
                      <h4 className="text-xl font-bold text-ink mb-3">
                        <CardTitleLink
                          slug={product.slug}
                          href={href}
                          className="text-ink hover:text-ink hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                        >
                          {product.title}
                        </CardTitleLink>
                      </h4>
                      <p className="text-ink-soft leading-relaxed mb-6 flex-1">
                        {product.description}
                      </p>
                      {externalCta ? (
                        <a
                          href={externalCta}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-primary font-semibold text-sm hover:gap-2 transition-all"
                        >
                          <span>{slotT('products.cta')}</span>
                          <Icon icon="lucide:arrow-right" />
                        </a>
                      ) : isStaticProductPage(product.slug) ? (
                        <a
                          href={href}
                          className="inline-flex items-center gap-1 text-primary font-semibold text-sm hover:gap-2 transition-all"
                        >
                          <span>{slotT('products.cta')}</span>
                          <Icon icon="lucide:arrow-right" />
                        </a>
                      ) : (
                        <Link
                          to={href}
                          className="inline-flex items-center gap-1 text-primary font-semibold text-sm hover:gap-2 transition-all"
                        >
                          <span>{slotT('products.cta')}</span>
                          <Icon icon="lucide:arrow-right" />
                        </Link>
                      )}
                    </Card>
                  </Reveal>
                )
              })}
            </div>
          </div>

          {/* 2c. 硬件與工具 */}
          <div>
            <h3 className="text-xl md:text-2xl font-bold text-ink mb-5">
              {slotT('products.offers.tools.title')}
            </h3>
            <div className="grid md:grid-cols-2 gap-gap-cards">
              {toolItems.map((product, idx) => {
                // A product whose button points at ANOTHER website (CSP).
                // Null for every product that stays on this site.
                const externalCta = productExternalCta(product.slug)
                return (
                <Reveal key={product.slug} delay={idx * 0.1}>
                  <Card hover bordered id={`product-${product.slug}`} className="h-full flex flex-col scroll-mt-[var(--sticky-header-offset)]">
                    <div className="flex items-start gap-4">
                      <div
                        className={`w-16 h-16 rounded-yada-xl bg-gradient-to-br ${product.accent_gradient} flex items-center justify-center flex-shrink-0 shadow-yada`}
                      >
                        <Icon
                          icon={product.icon}
                          className="text-surface"
                          style={{ fontSize: '2rem' }}
                        />
                      </div>
                      <div className="min-w-0 flex-1">
                        <h4 className="text-xl font-bold text-ink mb-2">
                          <CardTitleLink
                            slug={product.slug}
                            href={productHref(product.slug)}
                            className="text-ink hover:text-ink hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                          >
                            {product.title}
                          </CardTitleLink>
                        </h4>
                        <p className="text-ink-soft leading-relaxed mb-4">
                          {product.description}
                        </p>
                        {/* Every tool has a page of its own, so every
                            tool card carries the same call to action as
                            the service cards above. */}
                        {externalCta ? (
                          <a
                            href={externalCta}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-primary font-semibold text-sm hover:gap-2 transition-all"
                          >
                            <span>{slotT('products.cta')}</span>
                            <Icon icon="lucide:arrow-right" />
                          </a>
                        ) : isStaticProductPage(product.slug) ? (
                          <a
                            href={productHref(product.slug)}
                            className="inline-flex items-center gap-1 text-primary font-semibold text-sm hover:gap-2 transition-all"
                          >
                            <span>{slotT('products.cta')}</span>
                            <Icon icon="lucide:arrow-right" />
                          </a>
                        ) : (
                          <Link
                            to={productHref(product.slug)}
                            className="inline-flex items-center gap-1 text-primary font-semibold text-sm hover:gap-2 transition-all"
                          >
                            <span>{slotT('products.cta')}</span>
                            <Icon icon="lucide:arrow-right" />
                          </Link>
                        )}
                      </div>
                    </div>
                  </Card>
                </Reveal>
                )
              })}
            </div>
            <p className="mt-6 text-sm text-ink-muted leading-relaxed">
              {slotT('products.offers.tools.note')}
            </p>
          </div>
        </div>
      </Container>
    </section>
  )
}
